import { useSearchParams } from 'react-router-dom';

import { getApiFilters } from '~/shared/utils/apiFilters';
import { SelectOption } from '~/shared/utils/types';

import { apiFilterKey } from '../../../../common/serachParamKeys';
import {
  AUTOSCALING_STATE_PARAM_KEY,
  FilterStoredInURL,
  createCollapsedAutoscalingStateFilters,
  filterNonSpecificGroups,
  combineFilters,
} from '../utils/filterUtils';

function setAutoscalingUrlParams(
  updatedParams: URLSearchParams,
  stateIds: string[]
) {
  if (stateIds.length > 0) {
    updatedParams.set(AUTOSCALING_STATE_PARAM_KEY, stateIds.join(','));
  } else {
    updatedParams.delete(AUTOSCALING_STATE_PARAM_KEY);
  }
}

function buildFinalApiFilters(
  existingFiltersFromUrl: FilterStoredInURL[],
  stateIds: string[],
  pendingClusterNameFilters: SelectOption[],
  pendingVolumeIdFilters: SelectOption[],
  pendingVolumeTagFilters: SelectOption[],
  pendingAccountFilters: SelectOption[],
  pendingInstanceFilters: SelectOption[]
): SelectOption[] {
  const baseApiFilters = filterNonSpecificGroups(existingFiltersFromUrl);
  const autoscalingStateFilters = createCollapsedAutoscalingStateFilters(stateIds);

  return combineFilters(
    baseApiFilters,
    pendingClusterNameFilters,
    pendingVolumeIdFilters,
    pendingVolumeTagFilters,
    pendingAccountFilters,
    pendingInstanceFilters,
    autoscalingStateFilters
  );
}

function writeFilterParams(
  updatedParams: URLSearchParams,
  finalApiFilters: SelectOption[],
  stateIds: string[]
) {
  setAutoscalingUrlParams(updatedParams, stateIds);

  if (finalApiFilters.length > 0) {
    updatedParams.set(apiFilterKey, JSON.stringify(finalApiFilters));
    const processedFilters = getApiFilters(finalApiFilters);
    updatedParams.set('processed_filters', JSON.stringify(processedFilters));
  } else {
    updatedParams.delete(apiFilterKey);
    updatedParams.delete('processed_filters');
  }
}

export function useFilterUrlParams() {
  const [_searchParams, setSearchParams] = useSearchParams();

  const applyFilters = (
    pendingAutoscaleState: string[],
    pendingClusterNameFilters: SelectOption[],
    pendingVolumeIdFilters: SelectOption[],
    pendingVolumeTagFilters: SelectOption[],
    pendingAccountFilters: SelectOption[],
    pendingInstanceFilters: SelectOption[]
  ) => {
    setSearchParams(
      (prevParams) => {
        const updatedParams = new URLSearchParams(prevParams);

        const existingFiltersFromUrl = JSON.parse(
          prevParams.get(apiFilterKey) || '[]'
        ) as FilterStoredInURL[];

        const finalApiFilters = buildFinalApiFilters(
          existingFiltersFromUrl,
          pendingAutoscaleState,
          pendingClusterNameFilters,
          pendingVolumeIdFilters,
          pendingVolumeTagFilters,
          pendingAccountFilters,
          pendingInstanceFilters
        );

        writeFilterParams(
          updatedParams,
          finalApiFilters,
          pendingAutoscaleState
        );

        return updatedParams;
      },
      { replace: true }
    );
  };

  const resetFilters = () => {
    setSearchParams(
      (prev) => {
        const updatedParams = new URLSearchParams(prev);
        updatedParams.delete(AUTOSCALING_STATE_PARAM_KEY);
        updatedParams.delete('processed_filters');

        const existingFilters = JSON.parse(prev.get(apiFilterKey) || '[]') as FilterStoredInURL[];
        const nonSpecificFilters = filterNonSpecificGroups(existingFilters);

        if (nonSpecificFilters.length > 0) {
          updatedParams.set(apiFilterKey, JSON.stringify(nonSpecificFilters));
        } else {
          updatedParams.delete(apiFilterKey);
        }

        return updatedParams;
      },
      { replace: true }
    );
  };

  return { applyFilters, resetFilters };
}
